import { SidebarUsageAlert } from '@/components/sidebar-usage-alert';

interface OrgUsageBannerProps {
  orgId: string;
  percentage: number;
  /** Hide the Upgrade Plan CTA (e.g. on the billing page itself). Default true. */
  showCta?: boolean;
  /** The meter's unit (AIT-420). Defaults to the legacy message meter. */
  unitLabel?: 'messages' | 'actions';
  /** AIT-593: org-wide "is delivery stopped", straight from
   *  GET /organizations/:orgId/usage. Absent (pre-AIT-593 backend) makes no
   *  pause claim rather than guessing one. */
  channelsPaused?: boolean;
  /** AIT-638: live top-up reserve in `unitLabel`. A positive reserve absorbs
   *  the overage, so the banner must not read as "you are out". */
  reserveBalance?: number;
}

/**
 * Org usage banner. Surfaces when the org's monthly pool is high (>=75%) or
 * frozen (>=100%). Used in the org sidebar (admin-gated mount) and on the
 * billing page body. Layout + palette live in the shared SidebarUsageAlert.
 *
 * AIT-593: the pause claim comes from the server, org-wide. It used to be
 * `percentage >= 100`, which told a customer holding a paid top-up he was cut
 * off while the backend was still forwarding. Client-side substitutes were all
 * wrong: `overflowMode` and `reserveBalance` are billing numbers and the
 * billing engine's own pause paths contradict both, while the workspace
 * channel list is the wrong SCOPE -- the meter is org-pooled, but channels
 * hang off workspaces, so a paused sibling workspace is invisible to it.
 */
export function OrgUsageBanner({
  orgId,
  percentage,
  showCta = true,
  unitLabel = 'messages',
  channelsPaused = false,
  reserveBalance = 0,
}: OrgUsageBannerProps) {
  const isFrozen = channelsPaused;
  const hasReserve = !isFrozen && reserveBalance > 0;
  const onActions = unitLabel === 'actions';
  // Out of plan quota with no pause recorded against the channels.
  //
  // this states ONLY that, and deliberately promises
  // nothing about coverage. "No pause marker" is not the same as "someone is
  // paying for the overage" — an org can hit 100% before the asynchronous
  // hard stop has written its channel rows, or simply have no active channels
  // at all, and be sitting on overflowMode 'none' with an empty reserve.
  // Claiming automatic coverage there would be the same species of lie this
  // ticket exists to remove, just quieter.
  const overQuotaNoPause = !isFrozen && percentage >= 100;
  const unit = onActions ? 'action' : 'message';
  // say WHAT is observed, not WHY. The pause marker does
  // not mean the plan ran out — TopUpRechargeService pauses at its positive
  // runway threshold on a declined card, a hit spend cap, or no card on file,
  // which can land well below 100%. Claiming "you used every action" there is
  // wrong and sends the customer to the wrong remedy. One string for both
  // meters: a pause is a pause regardless of which meter the org is on.
  const message = isFrozen
    // the server flag is true when ANY channel is held
    // down, so "nothing is being sent or received" overclaims for an org whose
    // other channels are fine. "Some" is the weaker claim and stays true when
    // every channel is paused, so one string covers both without lying.
    // Names no meter. A pause stops delivery whatever the org is metered in,
    // and the previous wording told an actions org about "messages", which
    // contradicted the meter rendered directly above it.
    ? 'Some of your channels are paused. Nothing is being sent or received on those channels.'
    : overQuotaNoPause
      ? hasReserve
        ? `Your plan's ${unit}s are used up. Your top-up reserve (${reserveBalance.toLocaleString()} ${unit}s left) is covering the rest until it runs out or expires.`
        : `Your organization has used every ${unit} in this plan.`
      : onActions
        ? `Your organization has used ${percentage}% of this month's actions.${hasReserve ? ` Your top-up reserve (${reserveBalance.toLocaleString()} actions) covers anything above the plan until it runs out or expires.` : ''}`
        : `Your organization is at ${percentage}% of its monthly message limit.${hasReserve ? ` Your top-up reserve (${reserveBalance.toLocaleString()} messages) covers anything above the plan until it runs out or expires.` : ''}`;

  return (
    <SidebarUsageAlert
      orgId={orgId}
      // AIT-638: the 100 tier is red and undismissable because it means
      // "delivery is stopped" (AIT-636). A reserve-backed org over its plan
      // is still delivering, so it renders as the 90 band instead.
      percentage={hasReserve ? Math.min(percentage, 99) : percentage}
      frozen={isFrozen}
      message={message}
      showCta={showCta}
    />
  );
}
